import { useEffect, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { IconObjekte, IconPfeilRechts, IconPlus } from '../components/icons';
import { cn } from '../lib/cn';
import { DEMO, api, ApiFehler } from '../lib/api';
import { DEMO_OBJEKTE } from '../lib/demodaten';
import { formatQm } from '../lib/format';
import { demoHausListe, etagen, geschossKurz } from '../lib/haus';
import { transition, uebergangSanft } from '../lib/motion';
import { BankabgleichPanel, type LeaseV } from './Mietverhaeltnis';
import { ObjektAkte, type ObjektBereich } from './ObjektAkte';
import { type ApiObjektVoll, OBJEKTART_LABEL, ObjektFormular, objektartVon } from './ObjektFormular';
import { ObjektArchiv, demoObjektZustand } from './ObjektLoeschen';

/**
 * Objekte: Übersicht aller Häuser. Ein Klick öffnet die Objektakte mit der Hausansicht
 * (Etage für Etage), von dort jede Einheit mit Mietern, Zählern und Abrechnungen.
 */

export interface ObjektFokus {
  objektId: string;
  bereich: ObjektBereich;
  /** Wechselt bei jeder Anforderung, damit derselbe Fokus erneut greift. */
  nonce: number;
}

/** Vorschau: in dieser Sitzung angelegte Objekte (überleben das Neuladen der Liste). */
const demoEigene: ApiObjektVoll[] = [];

function demoObjekte(): ApiObjektVoll[] {
  const haeuser = new Map(demoHausListe().map((h) => [h.id, h]));
  const eigene = demoEigene
    .filter((o) => !demoObjektZustand.has(o.id))
    .map((o) => {
      const h = haeuser.get(o.id);
      return h ? { ...o, ...h.struktur, einheitenAnzahl: h.einheiten, vermietetAnzahl: h.vermietet, eigennutzungAnzahl: h.eigennutzung } : o;
    });
  return [...eigene, ...basisObjekte(haeuser)];
}

function basisObjekte(haeuser: Map<string, ReturnType<typeof demoHausListe>[number]>): ApiObjektVoll[] {
  return DEMO_OBJEKTE.filter((o) => !demoObjektZustand.has(o.id)).map((o) => {
    const h = haeuser.get(o.id);
    const [plz, ...ortTeile] = o.adresse.split(' ');
    return {
      id: o.id,
      bezeichnung: o.bezeichnung,
      strasse: o.bezeichnung,
      plz: plz ?? '',
      ort: ortTeile.join(' '),
      anschrift: `${o.bezeichnung}, ${o.adresse}`,
      baujahr: o.id === 'obj-1' ? 1962 : 1998,
      gesamtflaecheQm: o.wohnflaecheQm,
      co2Kennwert: o.co2Kennwert,
      bundesland: 'NW',
      mietspiegelQmCent: o.id === 'obj-1' ? 1180 : 1050,
      kappungsgrenzeProzent: o.id === 'obj-1' ? 20 : 15,
      darlehenRestCent: null,
      sollzinsProzent: null,
      tilgungProzent: null,
      ausstattung: o.id === 'obj-1' ? ['aufzug', 'solarthermie', 'pv'] : ['stellplatz'],
      ausstattungFrei: null,
      energieausweisArt: 'verbrauch',
      energieklasse: o.id === 'obj-1' ? 'C' : 'D',
      energieKwh: null,
      energieausweisGueltigBis: null,
      untergeschosse: h?.struktur.untergeschosse ?? 0,
      obergeschosse: h?.struktur.obergeschosse ?? null,
      dachgeschoss: h?.struktur.dachgeschoss ?? false,
      einheitenAnzahl: h?.einheiten ?? o.einheiten,
      vermietetAnzahl: h?.vermietet ?? o.vermietet,
      eigennutzungAnzahl: h?.eigennutzung ?? 0,
    };
  });
}

export function Objekte({
  fokus,
  onOeffneAbrechnungen,
}: {
  fokus?: ObjektFokus | null;
  onOeffneAbrechnungen?: () => void;
} = {}) {
  const reduziert = useReducedMotion();
  const [objekte, setObjekte] = useState<ApiObjektVoll[] | null>(DEMO ? demoObjekte() : null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [bankOffen, setBankOffen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [archivSchluessel, setArchivSchluessel] = useState(0);
  const [offen, setOffen] = useState<{ id: string; bereich: ObjektBereich; assistent: boolean; gewerbeImEg?: boolean; meldung?: string } | null>(
    fokus ? { id: fokus.objektId, bereich: fokus.bereich, assistent: false } : null,
  );

  async function listeLaden() {
    if (DEMO) {
      setObjekte(demoObjekte());
      return;
    }
    try {
      const d = await api.get<{ objekte: ApiObjektVoll[] }>('/objekte');
      setObjekte(d.objekte);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Objekte konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }

  useEffect(() => {
    void listeLaden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fokus) setOffen({ id: fokus.objektId, bereich: fokus.bereich, assistent: false });
  }, [fokus?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  if (offen) {
    return (
      <ObjektAkte
        key={offen.id}
        objektId={offen.id}
        vorlage={objekte?.find((o) => o.id === offen.id) ?? null}
        startBereich={offen.bereich}
        assistentStarten={offen.assistent}
        assistentGewerbeImEg={offen.gewerbeImEg}
        startMeldung={offen.meldung}
        onZurueck={() => {
          setOffen(null);
          void listeLaden();
        }}
        onObjektGeaendert={(o) => {
          if (DEMO) {
            const i = demoEigene.findIndex((x) => x.id === o.id);
            if (i >= 0) demoEigene[i] = { ...demoEigene[i], ...o };
          }
          setObjekte((l) => (l ?? []).map((x) => (x.id === o.id ? { ...x, ...o } : x)));
        }}
        onEntfernt={(text) => {
          const id = offen.id;
          setOffen(null);
          setMeldung(text);
          setObjekte((l) => (l ?? []).filter((x) => x.id !== id));
          setArchivSchluessel((k) => k + 1);
          void listeLaden();
        }}
        onOeffneAbrechnungen={onOeffneAbrechnungen}
      />
    );
  }

  return (
    <div>
      <Seitenkopf
        titel="Objekte"
        beschreibung="Ihre Häuser mit allen Etagen und Einheiten. Öffnen Sie ein Objekt, um Belegung, Mieter, Zähler und Abrechnungen je Wohnung zu sehen."
        aktion={
          <div className="flex flex-wrap gap-2">
            {(objekte?.length ?? 0) > 0 && (
              <Button variante="sekundaer" onClick={() => setBankOffen((b) => !b)} aria-expanded={bankOffen}>
                {bankOffen ? 'Kontoabgleich schließen' : 'Kontoauszug abgleichen'}
              </Button>
            )}
            <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen((o) => !o)}>
              Objekt anlegen
            </Button>
          </div>
        }
      />

      <AnimatePresence initial={false}>
        {formOffen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transition(!!reduziert, uebergangSanft)}
            className="overflow-hidden"
          >
            <ObjektFormular
              onAbbruch={() => setFormOffen(false)}
              onGespeichert={(o, info) => {
                if (DEMO) demoEigene.unshift(o);
                setObjekte((l) => [o, ...(l ?? [])]);
                setFormOffen(false);
                setMeldung(null);
                if (info?.einheitAngelegt) {
                  // Einfamilienhaus / Eigentumswohnung: Einheit steht, weiter mit Mieter und Zählern.
                  setOffen({
                    id: o.id,
                    bereich: 'haus',
                    assistent: false,
                    meldung: `${o.bezeichnung} angelegt. Tippen Sie auf die Einheit, um Mietverhältnis und Zähler zu erfassen.`,
                  });
                } else {
                  // Direkt weiter zum Aufbau des Hauses.
                  setOffen({ id: o.id, bereich: 'haus', assistent: true, gewerbeImEg: o.objektart === 'wgh' });
                }
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {bankOffen && <Kontoabgleich />}

      <AnimatePresence initial={false}>
        {meldung && (
          <motion.div
            key={meldung}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={transition(!!reduziert, uebergangSanft)}
            role="status"
            className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-success-soft px-3 py-2.5 text-sm text-success-soft-fg"
          >
            <span>{meldung}</span>
            <button
              type="button"
              onClick={() => setMeldung(null)}
              className="shrink-0 rounded-md px-1 font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              Schließen
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {laden && !objekte ? (
        <p className="text-sm text-fg-muted">Objekte werden geladen …</p>
      ) : fehler && !objekte ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : objekte && objekte.length === 0 ? (
        <EmptyState
          icon={<IconObjekte groesse={22} />}
          titel="Noch kein Objekt angelegt"
          beschreibung="Legen Sie Ihr erstes Haus an. Danach geben Sie Etagen und Wohnungen an und erfassen je Wohnung Mieter und Zähler."
          aktion={
            <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen(true)}>
              Objekt anlegen
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {(objekte ?? []).map((o, i) => (
            <ObjektKarte
              key={o.id}
              o={o}
              index={i}
              reduziert={!!reduziert}
              onOeffne={() => setOffen({ id: o.id, bereich: 'haus', assistent: false })}
            />
          ))}
        </div>
      )}

      <ObjektArchiv
        key={archivSchluessel}
        onMeldung={setMeldung}
        onWiederhergestellt={(o) => {
          setObjekte((l) => ((l ?? []).some((x) => x.id === o.id) ? l : [o, ...(l ?? [])]));
          void listeLaden();
        }}
      />
    </div>
  );
}

function ObjektKarte({
  o,
  index,
  reduziert,
  onOeffne,
}: {
  o: ApiObjektVoll;
  index: number;
  reduziert: boolean;
  onOeffne: () => void;
}) {
  const einheiten = o.einheitenAnzahl ?? 0;
  const vermietet = o.vermietetAnzahl ?? 0;
  const eigen = o.eigennutzungAnzahl ?? 0;
  const leer = Math.max(0, einheiten - vermietet - eigen);
  const art = objektartVon(o);
  const etagenText =
    o.obergeschosse === null
      ? 'Etagen nicht festgelegt'
      : (() => {
          const l = etagen(o);
          return l.length === 1 ? 'EG' : `${geschossKurz(l[l.length - 1])} bis ${geschossKurz(l[0])}`;
        })();

  function taste(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOeffne();
    }
  }

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={onOeffne}
      onKeyDown={taste}
      aria-label={`${o.bezeichnung} öffnen`}
      initial={reduziert ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: reduziert ? 0 : index * 0.05 }}
      whileHover={reduziert ? undefined : { y: -2 }}
      whileTap={reduziert ? undefined : { scale: 0.99 }}
      className="cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      <Card
        titel={o.bezeichnung}
        beschreibung={o.anschrift}
        aktion={o.energieklasse ? <Badge ton="info">Klasse {o.energieklasse}</Badge> : undefined}
      >
        {einheiten === 0 ? (
          <p className="text-sm text-fg-muted">Noch keine Einheiten — Haus aufbauen und Wohnungen anlegen.</p>
        ) : (
          <>
            <div className="flex h-2.5 overflow-hidden rounded-pill bg-surface-3" aria-hidden="true">
              <span className="bg-success" style={{ width: `${(vermietet / einheiten) * 100}%` }} />
              <span className="bg-accent" style={{ width: `${(eigen / einheiten) * 100}%` }} />
              <span className="bg-warning" style={{ width: `${(leer / einheiten) * 100}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <Zahl label="Einheiten" wert={String(einheiten)} />
              <Zahl label="Vermietet" wert={String(vermietet)} />
              <Zahl label="Leerstand" wert={String(leer)} warnung={leer > 0} />
            </div>
          </>
        )}
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3 text-sm">
          <span className="min-w-0 truncate text-fg-muted">
            {[art ? OBJEKTART_LABEL[art] : null, art === 'efh' || art === 'etw' ? null : etagenText, formatQm(o.gesamtflaecheQm)]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 font-medium text-accent">
            Haus öffnen <IconPfeilRechts groesse={16} />
          </span>
        </div>
      </Card>
    </motion.div>
  );
}

function Zahl({ label, wert, warnung }: { label: string; wert: string; warnung?: boolean }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-fg-subtle">{label}</p>
      <p className={cn('text-lg font-semibold tabellenziffern', warnung ? 'text-warning' : 'text-fg')}>{wert}</p>
    </div>
  );
}

interface ApiMv {
  id: string;
  einheitId: string;
  einheitBezeichnung?: string;
  mieterName?: string;
  beginn: string;
  ende: string | null;
  kaltmieteCent: number;
  vzNebenkostenCent: number;
  vzHeizkostenCent: number;
  personenzahl: number;
  mietart: LeaseV['mietart'];
  letzteErhoehungAm: string | null;
  kautionCent: number;
  kautionStatus: LeaseV['kautionStatus'];
}

const DEMO_LAUFEND: LeaseV[] = [
  { id: 'mv-1', einheitId: 'e-1', einheit: 'EG links', mieter: 'Katrin Berger', beginn: '2021-04-01', ende: null, kaltmieteCent: 76000, vzNkCent: 17000, vzHeizCent: 9000, personen: 2, mietart: 'normal', letzteErhoehungAm: null, kautionCent: 228000, kautionStatus: 'angelegt' },
  { id: 'mv-2', einheitId: 'e-3', einheit: '1. OG links', mieter: 'Murat Yildiz', beginn: '2023-09-15', ende: null, kaltmieteCent: 82000, vzNkCent: 19500, vzHeizCent: 10500, personen: 3, mietart: 'normal', letzteErhoehungAm: null, kautionCent: 246000, kautionStatus: 'angelegt' },
];

/** Kontoauszug über alle Objekte: Zahlungseingänge den laufenden Mietverhältnissen zuordnen. */
function Kontoabgleich() {
  const [leases, setLeases] = useState<LeaseV[]>(DEMO ? DEMO_LAUFEND : []);

  useEffect(() => {
    if (DEMO) return;
    const heute = new Date().toISOString().slice(0, 10);
    api
      .get<{ mietverhaeltnisse: ApiMv[] }>(`/mietverhaeltnisse?stichtag=${heute}`)
      .then((d) =>
        setLeases(
          d.mietverhaeltnisse.map((x) => ({
            id: x.id,
            einheitId: x.einheitId,
            einheit: x.einheitBezeichnung ?? 'Einheit',
            mieter: x.mieterName ?? 'Mieter',
            beginn: x.beginn,
            ende: x.ende,
            kaltmieteCent: x.kaltmieteCent,
            vzNkCent: x.vzNebenkostenCent,
            vzHeizCent: x.vzHeizkostenCent,
            personen: x.personenzahl,
            mietart: x.mietart ?? 'normal',
            letzteErhoehungAm: x.letzteErhoehungAm,
            kautionCent: x.kautionCent ?? 0,
            kautionStatus: x.kautionStatus ?? 'offen',
          })),
        ),
      )
      .catch(() => setLeases([]));
  }, []);

  return <BankabgleichPanel leases={leases} onGebucht={() => undefined} />;
}

export default Objekte;
